import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { formatCents } from "@shared/money";
import { venueInput, venuePatch } from "@shared/schemas/catalog";
import { validateVenue } from "@shared/validation/admin";
import { evaluation, parseInput } from "@shared/validation/issues";
import { errorMessage } from "@web/lib/api";
import { changedValues, useFormValidation } from "@web/lib/formValidation";
import { recordValidationQuery, useBatchValidation } from "@web/lib/validation";
import { api, membersQuery, venuesQuery, type Venue } from "@web/lib/queries";
import { useMe } from "@web/lib/session";
import { BottomSheet, Button, ColorDot, EmptyState, Inline, MoneyField, Page, Picker, Row, RowGroup, Skeleton, Stack, StatusChip, TextArea, TextField, Toggle, UnsavedChanges, ValidationBadge, ValidationSummary, useToast } from "@web/ui";
import { AdminsOnly } from "./Settings";

const EMPTY = { name: "", shortName: "", slug: "", address: "", city: "", state: "", zip: "", color: "#A6791B", capacity: "", damageDepositDefault: 0 as number | null, defaultAssigneeMemberId: "", active: true, sort: "0", aliases: "", notes: "" };
type Form = typeof EMPTY;
const fromVenue = (v: Venue): Form => ({
  name: v.name,
  shortName: v.shortName,
  slug: v.slug,
  address: v.address ?? "",
  city: v.city ?? "",
  state: v.state ?? "",
  zip: v.zip ?? "",
  color: v.color,
  capacity: v.capacity?.toString() ?? "",
  damageDepositDefault: v.damageDepositDefault,
  defaultAssigneeMemberId: v.defaultAssigneeMemberId ?? "",
  active: Boolean(v.active),
  sort: String(v.sort),
  aliases: v.aliases.join(", "),
  notes: v.notes ?? "",
});

/** Settings › Venues: list + edit sheet (SPEC §3.3). */
export function SettingsVenues() {
  const me = useMe();
  const q = useQuery(venuesQuery(true));
  const checks = useBatchValidation(me.companyAdmin ? (q.data ?? []).map(row => ({ entityType: "venue", id: row.id })) : []);
  const [editing, setEditing] = useState<Venue | "new" | null>(null);
  if (!me.companyAdmin) return <AdminsOnly />;
  return (
    <Page>
      <Stack gap={3}>
        {(checks.isError || checks.data?.checksComplete === false) && <ValidationSummary result={checks.data} unavailable onRetry={() => void checks.refetch()} />}
        {q.isError ? (
          <EmptyState title="Venues could not be loaded" body={errorMessage(q.error)} action={<Button onClick={() => void q.refetch()}>Try again</Button>} />
        ) : !q.data ? (
          <Skeleton count={3} />
        ) : q.data.length === 0 ? (
          <EmptyState title="No venues yet" body="Add your first venue to start booking." />
        ) : (
          <RowGroup>
            {q.data.map((v) => (
              <Row key={v.id} icon={<ColorDot color={v.color} />} title={v.name} subtitle={`${v.shortName} · ${[v.city, v.state].filter(Boolean).join(", ")}${v.damageDepositDefault ? ` · damage ${formatCents(v.damageDepositDefault)}` : ""}`} trailing={<Stack gap={1}>{!v.active && <StatusChip>Inactive</StatusChip>}{checks.isFetching && !checks.data ? <StatusChip>Checking…</StatusChip> : <ValidationBadge result={checks.data?.records.find(r => r.entity.type === "venue" && r.entity.id === v.id)} unavailable={checks.isError} />}</Stack>} onClick={() => setEditing(v)} />
            ))}
          </RowGroup>
        )}
        <Button variant="secondary" icon={<Plus aria-hidden size={18} />} onClick={() => setEditing("new")}>
          Add venue
        </Button>
      </Stack>
      {editing && <VenueSheet key={editing === "new" ? "new" : editing.id} venue={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </Page>
  );
}

function VenueSheet({ venue, onClose }: { venue: Venue | null; onClose: () => void }) {
  const toast = useToast();
  const members = useQuery(membersQuery);
  const saved = useQuery(recordValidationQuery("venue", venue?.id ?? ""));
  const [f, setF] = useState<Form>(venue ? fromVenue(venue) : EMPTY);
  const nul = (s: string) => s.trim() || null;
  const toBody = (f: Form) => ({
      name: f.name,
      shortName: f.shortName,
      slug: f.slug.trim() || undefined,
      address: nul(f.address),
      city: nul(f.city),
      state: nul(f.state),
      zip: nul(f.zip),
      color: f.color,
      capacity: f.capacity ? Number(f.capacity) : null,
      damageDepositDefault: f.damageDepositDefault,
      defaultAssigneeMemberId: f.defaultAssigneeMemberId || null,
      active: f.active,
      sort: Number(f.sort),
      aliases: f.aliases.split(",").map((s) => s.trim()).filter(Boolean),
      notes: nul(f.notes),
    });
  const body = venue ? changedValues(toBody(f), toBody(fromVenue(venue))) : toBody(f);
  const parsed = parseInput(venue ? venuePatch : venueInput, body, { type: "venue", id: venue?.id }, "venue.save");
  const validation = useFormValidation(evaluation([...parsed.issues, ...validateVenue({ ...venue, ...body }, { before: venue ?? undefined, changedFields: Object.keys(body) }).issues], false));
  const { busy, field } = validation;
  const set = <K extends keyof Form>(k: K, v: Form[K]) => validation.edit(() => setF((x) => ({ ...x, [k]: v })));
  async function save() {
    if (!parsed.success || !validation.begin("venue.save")) return;
    try {
      if (venue) await api.patchVenue(venue.id, parsed.data);
      else await api.createVenue(venueInput.parse(body));
      toast.success(venue ? "Venue saved" : "Venue added");
      onClose();
    } catch (e) {
      validation.reject(e);
      toast.error(errorMessage(e));
    } finally {
      validation.finish();
    }
  }

  return (
    <BottomSheet
      open
      onClose={() => { if (!busy) onClose(); }}
      title={venue ? "Edit venue" : "New venue"}
      footer={
        <Button loading={busy} onClick={save}>
          {venue ? "Save venue" : "Add venue"}
        </Button>
      }
    >
      <Stack gap={3}>
        <UnsavedChanges dirty={!busy && JSON.stringify(f) !== JSON.stringify(venue ? fromVenue(venue) : EMPTY)} />
        {venue && <ValidationSummary result={saved.data} loading={saved.isFetching} unavailable={saved.isError} onRetry={() => void saved.refetch()} saved />}
        <ValidationSummary result={validation.result} local unavailable={validation.unavailable} />
        <TextField label="Name" value={f.name} onChange={(e) => set("name", e.target.value)} autoFocus={!venue} {...field("name")} />
        <Inline gap={2} align="start">
          <TextField label="Short name" className="flex-1" value={f.shortName} onChange={(e) => set("shortName", e.target.value)} hint="Calendar and chips" {...field("shortName")} />
          <TextField label="Slug" className="flex-1" value={f.slug} onChange={(e) => set("slug", e.target.value)} hint="Used by the lead API" {...field("slug")} />
        </Inline>
        <TextField label="Address" value={f.address} onChange={(e) => set("address", e.target.value)} {...field("address")} />
        <Inline gap={2} align="start">
          <TextField label="City" className="flex-1" value={f.city} onChange={(e) => set("city", e.target.value)} {...field("city")} />
          <TextField label="State" className="flex-1" value={f.state} onChange={(e) => set("state", e.target.value)} {...field("state")} />
          <TextField label="ZIP" className="flex-1" inputMode="numeric" value={f.zip} onChange={(e) => set("zip", e.target.value)} {...field("zip")} />
        </Inline>
        <Inline gap={2} align="start">
          <TextField label="Color" type="color" className="flex-1" value={f.color} onChange={(e) => set("color", e.target.value)} {...field("color")} />
          <TextField label="Capacity" type="number" inputMode="numeric" min={0} className="flex-1" value={f.capacity} onChange={(e) => set("capacity", e.target.value)} {...field("capacity")} />
        </Inline>
        <MoneyField label="Damage deposit default" value={f.damageDepositDefault} onChange={(v) => set("damageDepositDefault", v)} {...field("damageDepositDefault")} />
        <Picker label="Default assignee" placeholder="—" value={f.defaultAssigneeMemberId} onChange={(v) => set("defaultAssigneeMemberId", v)} options={(members.data ?? []).filter((m) => m.active).map((m) => ({ value: m.id, label: m.name }))} {...field("defaultAssigneeMemberId")} />
        <TextField label="Aliases" value={f.aliases} onChange={(e) => set("aliases", e.target.value)} hint="Comma-separated; other names the sites and ledger use" {...field("aliases")} />
        <TextField label="Sort" type="number" inputMode="numeric" value={f.sort} onChange={(e) => set("sort", e.target.value)} {...field("sort")} />
        <TextArea label="Notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} {...field("notes")} />
        <Toggle label="Active" checked={f.active} onChange={(v) => set("active", v)} disabled={busy} />
      </Stack>
    </BottomSheet>
  );
}
